import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { CostCalculator } from "@/components/CostCalculator";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import { ComplianceSelector } from "@/components/ComplianceFrameworksDisplay";
import { 
  Server, Shield, Database, Clock, DollarSign, 
  Code, Settings, ChevronRight, Loader2, Sparkles,
  Layers, Zap, Info, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
  "Web Application", "API Service", "Data Pipeline", "AI/ML Platform",
  "E-commerce", "SaaS Platform", "Mobile Backend", "IoT Platform",
  "Microservices", "Serverless"
];

const cloudPlatforms = ["AWS", "Azure", "GCP", "OCI", "IBM Cloud", "Alibaba Cloud"];

const complianceOptions = [
  // Most Common
  "SOC 2 Type II", "ISO 27001", "GDPR", "HIPAA", "PCI-DSS", "SOC 1",
  // Government/Federal
  "FedRAMP", "NIST 800-53", "NIST CSF", "FISMA", "CMMC", "CJIS", "ITAR",
  // Healthcare
  "HITRUST CSF", "HITECH",
  // Financial
  "SOX", "GLBA", "FFIEC", "Basel III", "SWIFT CSP",
  // Privacy
  "CCPA/CPRA", "LGPD", "PIPEDA", "PDPA",
  // Cloud Security
  "CSA STAR", "CSA CCM", "CIS Controls", "CIS Benchmarks",
  // Application Security
  "OWASP Top 10", "OWASP ASVS", "SANS Top 25",
  // Industry-Specific
  "NERC CIP", "IEC 62443", "TISAX"
];

const authOptions = ["SSO", "MFA", "OAuth2", "SAML", "API Keys", "JWT"];

const drStrategies = ["Pilot Light", "Warm Standby", "Active-Active", "Active-Passive", "Backup & Restore"];

const computePreferences = ["Serverless", "Kubernetes", "VMs", "Containers", "Hybrid"];

const deploymentStrategies = ["Blue/Green", "Canary", "Rolling", "Recreate", "A/B Testing"];

// New predefined options for dropdown menus
const userVolumeOptions = [
  "< 1K users", "1K - 10K users", "10K - 100K users", "100K - 1M users", 
  "1M - 10M users", "10M+ users"
];

const trafficPatternOptions = [
  "Steady", "Variable", "Spiky", "Seasonal", "Unpredictable", "Batch/Scheduled"
];

const geoRequirementOptions = [
  "Single region", "Multi-region (same continent)", "Multi-region (global)", 
  "US only", "EU only", "APAC only", "Global with compliance restrictions"
];

const latencyTargetOptions = [
  "< 50ms (real-time)", "< 100ms (interactive)", "< 200ms (standard)", 
  "< 500ms (acceptable)", "< 1s (batch)", "> 1s (background)"
];

const cicdToolOptions = [
  "GitHub Actions", "GitLab CI", "Jenkins", "CircleCI", "Azure DevOps", 
  "AWS CodePipeline", "ArgoCD", "Tekton", "Buildkite"
];

const dataVolumeOptions = [
  "< 10GB", "10GB - 100GB", "100GB - 500GB", "500GB - 1TB", 
  "1TB - 10TB", "10TB - 100TB", "100TB+"
];

const rpoOptions = [
  "0 (real-time/synchronous)", "< 1 minute", "< 15 minutes", "< 1 hour", 
  "< 4 hours", "< 24 hours", "< 7 days"
];

const rtoOptions = [
  "< 1 minute (instant failover)", "< 15 minutes", "< 1 hour", "< 4 hours", 
  "< 8 hours", "< 24 hours", "< 72 hours"
];

const dataSensitivityOptions = [
  "Public", "Internal", "Confidential", "Highly Confidential", "Regulated"
];

const budgetPriorityOptions = [
  "Cost Optimized", "Balanced", "Performance Optimized"
];

const budgetRangeOptions = [
  "< $1,000/mo", "$1,000 - $5,000/mo", "$5,000 - $20,000/mo", 
  "$20,000 - $50,000/mo", "$50,000 - $100,000/mo", "> $100,000/mo"
];

const languageRuntimeOptions = [
  "Node.js", "Python", "Java", "Go", "C#/.NET", "Ruby", "PHP", 
  "Rust", "Scala", "Kotlin", "Multiple"
];

export const Phase1Form = ({ project, onCreateProject, onUpdateProject, onNext, creating, selectedTemplate, targetTab, onTabChanged }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    project_name: "",
    project_type: "",
    business_narrative: { intake: "", processing: "", outcome: "" },
    cloud_platform: "",
    is_migration: false,
    scale: { user_volume: "", traffic_patterns: [], geo_requirements: "", latency_targets: "" },
    security: { data_sensitivity: "", compliance: [], auth: [] },
    data_storage: { database_type: "", volume: "", rpo: "", rto: "" },
    availability: { uptime_sla: "", dr_strategy: "" },
    budget: { priority: "", range: "" },
    tech_stack: { language_runtime: [], compute_preference: [] },
    ops: { monitoring_level: "", cicd_tools: [], deployment_strategy: [] }
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [showTemplateBanner, setShowTemplateBanner] = useState(!!selectedTemplate);

  // Handle navigation from remediation (targetTab)
  useEffect(() => {
    if (targetTab && targetTab !== activeTab) {
      setActiveTab(targetTab);
      if (onTabChanged) {
        onTabChanged();
      }
    }
  }, [targetTab]);

  useEffect(() => {
    if (project?.phase1) {
      setFormData(project.phase1);
      setShowTemplateBanner(false);
    } else if (selectedTemplate) {
      // Pre-fill form with template data
      const templatePhase1 = selectedTemplate.phase1 || {};
      setFormData(prev => ({
        ...prev,
        project_type: templatePhase1.project_type || "",
        business_narrative: templatePhase1.business_narrative || prev.business_narrative,
        scale: templatePhase1.scale || prev.scale,
        security: templatePhase1.security || prev.security,
        data_storage: templatePhase1.data_storage || prev.data_storage,
        availability: templatePhase1.availability || prev.availability,
        budget: templatePhase1.budget || prev.budget,
        tech_stack: templatePhase1.tech_stack || prev.tech_stack,
        ops: templatePhase1.ops || prev.ops
      }));
    }
  }, [project, selectedTemplate]);

  const updateField = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const toggleArrayItem = (path, item) => {
    setFormData(prev => {
      const keys = path.split('.');
      let current = prev;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      const arr = current[keys[keys.length - 1]] || [];
      const newArr = arr.includes(item) 
        ? arr.filter(i => i !== item)
        : [...arr, item];
      
      const newData = { ...prev };
      current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = newArr;
      return newData;
    });
  };

  const handleSubmit = async () => {
    if (!formData.project_name) {
      toast.error("Project name is required");
      return;
    }

    try {
      setSaving(true);
      
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (!project) {
        const newProject = await onCreateProject(formData.project_name);
        if (newProject) {
          await axios.put(`${API}/projects/${newProject.id}/phase1`, { phase1: formData }, { headers });
          onUpdateProject({ ...newProject, phase1: formData });
        }
      } else {
        await axios.put(`${API}/projects/${project.id}/phase1`, { phase1: formData }, { headers });
        onUpdateProject({ ...project, phase1: formData });
        toast.success("Project saved!");
      }
      
      onNext();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have access to this project");
      } else {
        toast.error("Failed to save project");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Template Banner */}
      {showTemplateBanner && selectedTemplate && (
        <div className="mb-6 bg-gradient-to-r from-[#00f0ff]/10 to-[#7000ff]/10 border border-[#00f0ff]/20 rounded-sm p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[#00f0ff]/20 flex items-center justify-center">
                <Layers className="w-5 h-5 text-[#00f0ff]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{selectedTemplate.name}</span>
                  <Badge className="bg-[#00f0ff]/10 text-[#00f0ff] border-[#00f0ff]/30 text-xs">
                    Template
                  </Badge>
                </div>
                <p className="text-xs text-zinc-400">{selectedTemplate.description}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-zinc-500 hover:text-white"
              onClick={() => setShowTemplateBanner(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Phase 1: Project Intake</h1>
        <p className="text-zinc-500">Define your cloud infrastructure requirements</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form - Takes 2 columns */}
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-zinc-900/50 border border-white/5 p-1">
              <TabsTrigger value="basic" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black">
                <Server className="w-4 h-4 mr-2" />
                Basic Info
              </TabsTrigger>
              <TabsTrigger value="scale" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black">
                <Settings className="w-4 h-4 mr-2" />
                Scale & Ops
              </TabsTrigger>
              <TabsTrigger value="security" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black">
                <Shield className="w-4 h-4 mr-2" />
                Security
              </TabsTrigger>
              <TabsTrigger value="data" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black">
                <Database className="w-4 h-4 mr-2" />
                Data & DR
              </TabsTrigger>
              <TabsTrigger value="budget" className="data-[state=active]:bg-[#00f0ff] data-[state=active]:text-black">
                <DollarSign className="w-4 h-4 mr-2" />
                Budget & Stack
              </TabsTrigger>
            </TabsList>

        {/* Basic Info Tab */}
        <TabsContent value="basic" className="space-y-6">
          <Card className="bg-[#09090b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Project Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Project Name *</Label>
                  <Input
                    value={formData.project_name}
                    onChange={(e) => updateField('project_name', e.target.value)}
                    placeholder="e.g., E-commerce Platform v2"
                    className="bg-black/50 border-white/10 text-white focus:border-[#00f0ff]/50"
                    data-testid="project-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Project Type</Label>
                  <Select
                    value={formData.project_type}
                    onValueChange={(v) => updateField('project_type', v)}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="project-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {projectTypes.map(type => (
                        <SelectItem key={type} value={type} className="text-white hover:bg-white/10">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Cloud Platform</Label>
                  <Select
                    value={formData.cloud_platform}
                    onValueChange={(v) => updateField('cloud_platform', v)}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="cloud-platform-select">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {cloudPlatforms.map(platform => (
                        <SelectItem key={platform} value={platform} className="text-white hover:bg-white/10">
                          {platform}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-3 pt-8">
                  <Checkbox
                    id="migration"
                    checked={formData.is_migration}
                    onCheckedChange={(checked) => updateField('is_migration', checked)}
                    className="border-white/20 data-[state=checked]:bg-[#00f0ff] data-[state=checked]:border-[#00f0ff]"
                    data-testid="migration-checkbox"
                  />
                  <Label htmlFor="migration" className="text-zinc-300 cursor-pointer">
                    This is a migration project (not greenfield)
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#09090b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Business Narrative</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Intake (What goes in?)</Label>
                <Textarea
                  value={formData.business_narrative.intake}
                  onChange={(e) => updateField('business_narrative.intake', e.target.value)}
                  placeholder="e.g., User uploads, API requests, IoT sensor data..."
                  className="bg-black/50 border-white/10 text-white min-h-[80px]"
                  data-testid="narrative-intake"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Processing (What happens?)</Label>
                <Textarea
                  value={formData.business_narrative.processing}
                  onChange={(e) => updateField('business_narrative.processing', e.target.value)}
                  placeholder="e.g., Data transformation, ML inference, business logic..."
                  className="bg-black/50 border-white/10 text-white min-h-[80px]"
                  data-testid="narrative-processing"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Outcome (What comes out?)</Label>
                <Textarea
                  value={formData.business_narrative.outcome}
                  onChange={(e) => updateField('business_narrative.outcome', e.target.value)}
                  placeholder="e.g., Dashboard updates, notifications, reports..."
                  className="bg-black/50 border-white/10 text-white min-h-[80px]"
                  data-testid="narrative-outcome"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scale & Ops Tab */}
        <TabsContent value="scale" className="space-y-6">
          <Card className="bg-[#09090b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Scale Requirements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-zinc-300">User Volume</Label>
                  <Select
                    value={formData.scale.user_volume}
                    onValueChange={(v) => updateField('scale.user_volume', v)}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="user-volume-select">
                      <SelectValue placeholder="Select user volume" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {userVolumeOptions.map(opt => (
                        <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Geo Requirements</Label>
                  <Select
                    value={formData.scale.geo_requirements}
                    onValueChange={(v) => updateField('scale.geo_requirements', v)}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="geo-requirements-select">
                      <SelectValue placeholder="Select geo requirement" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {geoRequirementOptions.map(opt => (
                        <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Latency Targets</Label>
                  <Select
                    value={formData.scale.latency_targets}
                    onValueChange={(v) => updateField('scale.latency_targets', v)}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="latency-targets-select">
                      <SelectValue placeholder="Select latency target" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      {latencyTargetOptions.map(opt => (
                        <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <MultiSelectCheckbox
                label="Traffic Patterns (select all that apply)"
                options={trafficPatternOptions}
                selected={formData.scale.traffic_patterns || []}
                onChange={(values) => updateField('scale.traffic_patterns', values)}
                showOther={true}
                otherPlaceholder="Add custom traffic pattern..."
                columns={3}
              />
            </CardContent>
          </Card>

          <Card className="bg-[#09090b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Operations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-zinc-300">Monitoring Level</Label>
                <Select
                  value={formData.ops.monitoring_level}
                  onValueChange={(v) => updateField('ops.monitoring_level', v)}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white max-w-xs" data-testid="monitoring-select">
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {["Basic", "Standard", "Advanced", "Enterprise"].map(level => (
                      <SelectItem key={level} value={level} className="text-white hover:bg-white/10">
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <MultiSelectCheckbox
                label="CI/CD Tools (select all that apply)"
                options={cicdToolOptions}
                selected={formData.ops.cicd_tools || []}
                onChange={(values) => updateField('ops.cicd_tools', values)}
                showOther={true}
                otherPlaceholder="Add custom CI/CD tool..."
                columns={3}
              />
              
              <MultiSelectCheckbox
                label="Deployment Strategies (select all that apply)"
                options={deploymentStrategies}
                selected={formData.ops.deployment_strategy || []}
                onChange={(values) => updateField('ops.deployment_strategy', values)}
                showOther={true}
                otherPlaceholder="Add custom strategy..."
                columns={3}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          <Card className="bg-[#09090b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Security Requirements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-zinc-300">Data Sensitivity</Label>
                <Select
                  value={formData.security.data_sensitivity}
                  onValueChange={(v) => updateField('security.data_sensitivity', v)}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="data-sensitivity-select">
                    <SelectValue placeholder="Select sensitivity level" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {["Public", "Internal", "Confidential", "Highly Confidential", "Regulated"].map(level => (
                      <SelectItem key={level} value={level} className="text-white hover:bg-white/10">
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-zinc-300">Compliance Requirements</Label>
                {/* DRAFT: Enhanced Compliance Selector with Categories */}
                <ComplianceSelector
                  selectedFrameworks={formData.security.compliance || []}
                  onSelectionChange={(selected) => updateField('security.compliance', selected)}
                />
              </div>

              <div className="space-y-3">
                <Label className="text-zinc-300">Authentication Methods</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {authOptions.map(option => (
                    <div key={option} className="flex items-center space-x-2">
                      <Checkbox
                        id={`auth-${option}`}
                        checked={formData.security.auth?.includes(option)}
                        onCheckedChange={() => toggleArrayItem('security.auth', option)}
                        className="border-white/20 data-[state=checked]:bg-[#00f0ff] data-[state=checked]:border-[#00f0ff]"
                        data-testid={`auth-${option}`}
                      />
                      <Label htmlFor={`auth-${option}`} className="text-zinc-400 text-sm cursor-pointer">
                        {option}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data & DR Tab */}
        <TabsContent value="data" className="space-y-6">
          <Card className="bg-[#09090b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Data Storage</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-zinc-300">Database Type</Label>
                <Select
                  value={formData.data_storage.database_type}
                  onValueChange={(v) => updateField('data_storage.database_type', v)}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="database-type-select">
                    <SelectValue placeholder="Select database" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {["SQL (PostgreSQL)", "SQL (MySQL)", "NoSQL (MongoDB)", "NoSQL (DynamoDB)", "Graph (Neo4j)", "Time-series", "Multi-model"].map(db => (
                      <SelectItem key={db} value={db} className="text-white hover:bg-white/10">
                        {db}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Data Volume</Label>
                <Select
                  value={formData.data_storage.volume}
                  onValueChange={(v) => updateField('data_storage.volume', v)}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="data-volume-select">
                    <SelectValue placeholder="Select data volume" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {dataVolumeOptions.map(opt => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">RPO (Recovery Point Objective)</Label>
                <Select
                  value={formData.data_storage.rpo}
                  onValueChange={(v) => updateField('data_storage.rpo', v)}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="rpo-select">
                    <SelectValue placeholder="Select RPO" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {rpoOptions.map(opt => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">RTO (Recovery Time Objective)</Label>
                <Select
                  value={formData.data_storage.rto}
                  onValueChange={(v) => updateField('data_storage.rto', v)}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="rto-select">
                    <SelectValue placeholder="Select RTO" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {rtoOptions.map(opt => (
                      <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#09090b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Availability & DR</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-zinc-300">Uptime SLA</Label>
                <Select
                  value={formData.availability.uptime_sla}
                  onValueChange={(v) => updateField('availability.uptime_sla', v)}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="uptime-sla-select">
                    <SelectValue placeholder="Select SLA" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {["99% (3.65 days/year)", "99.9% (8.76 hours/year)", "99.95% (4.38 hours/year)", "99.99% (52.6 min/year)", "99.999% (5.26 min/year)"].map(sla => (
                      <SelectItem key={sla} value={sla} className="text-white hover:bg-white/10">
                        {sla}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">DR Strategy</Label>
                <Select
                  value={formData.availability.dr_strategy}
                  onValueChange={(v) => updateField('availability.dr_strategy', v)}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="dr-strategy-select">
                    <SelectValue placeholder="Select strategy" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {drStrategies.map(strategy => (
                      <SelectItem key={strategy} value={strategy} className="text-white hover:bg-white/10">
                        {strategy}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Budget & Stack Tab */}
        <TabsContent value="budget" className="space-y-6">
          <Card className="bg-[#09090b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Budget</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-zinc-300">Priority</Label>
                <Select
                  value={formData.budget.priority}
                  onValueChange={(v) => updateField('budget.priority', v)}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="budget-priority-select">
                    <SelectValue placeholder="Cost vs Performance" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {["Cost Optimized", "Balanced", "Performance Optimized"].map(priority => (
                      <SelectItem key={priority} value={priority} className="text-white hover:bg-white/10">
                        {priority}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Monthly Budget Range</Label>
                <Select
                  value={formData.budget.range}
                  onValueChange={(v) => updateField('budget.range', v)}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="budget-range-select">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {["< $1,000", "$1,000 - $5,000", "$5,000 - $20,000", "$20,000 - $50,000", "$50,000 - $100,000", "> $100,000"].map(range => (
                      <SelectItem key={range} value={range} className="text-white hover:bg-white/10">
                        {range}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#09090b] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-lg">Tech Stack</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <MultiSelectCheckbox
                label="Language / Runtime (select all that apply)"
                options={languageRuntimeOptions}
                selected={formData.tech_stack.language_runtime || []}
                onChange={(values) => updateField('tech_stack.language_runtime', values)}
                showOther={true}
                otherPlaceholder="Add custom language/runtime..."
                columns={4}
              />
              
              <MultiSelectCheckbox
                label="Compute Preference (select all that apply)"
                options={computePreferences}
                selected={formData.tech_stack.compute_preference || []}
                onChange={(values) => updateField('tech_stack.compute_preference', values)}
                showOther={true}
                otherPlaceholder="Add custom compute type..."
                columns={3}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
            <div className="text-sm text-zinc-500">
              Fill in as much detail as possible for better AI recommendations
            </div>
            <Button
              onClick={handleSubmit}
              disabled={saving || creating || !formData.project_name}
              className="bg-[#00f0ff] text-black font-semibold hover:bg-[#00f0ff]/90 hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] rounded-sm px-6"
              data-testid="phase1-next-btn"
            >
              {saving || creating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate AI Follow-ups
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Cost Calculator Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <CostCalculator phase1Data={formData} />
            
            {/* Quick Tips */}
            <Card className="bg-[#09090b] border-white/5 mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#00f0ff]" />
                  Quick Tips
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-zinc-500">
                  Select a <span className="text-[#00f0ff]">cloud platform</span> to see real-time cost estimates
                </p>
                <p className="text-xs text-zinc-500">
                  Higher <span className="text-[#00f0ff]">uptime SLA</span> requirements increase costs significantly
                </p>
                <p className="text-xs text-zinc-500">
                  <span className="text-[#00ff9d]">Serverless</span> is most cost-effective for variable workloads
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
